// Логове (04 §7) — what the client sites report back.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import { MOCK_LOGS } from '../../lib/mockData-ops.js';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export interface LogRow {
  id: number;
  site_id: number;
  level: LogLevel;
  category: string | null;
  message: string;
  context: Record<string, unknown>;
  fingerprint: string | null;
  source: 'browser' | 'server';
  happened_at: string;
}

export interface LogFilters {
  level: LogLevel | '';
  source: '' | 'browser' | 'server';
  category: string;
  search: string;
  contextKey: string;
  contextValue: string;
  sinceHours: number;
}

export const EMPTY_LOG_FILTERS: LogFilters = {
  level: '',
  source: '',
  category: '',
  search: '',
  contextKey: '',
  contextValue: '',
  sinceHours: 24,
};

const DEMO_SAVED_QUERIES: SavedQuery[] = [
  {
    id: 1,
    name: 'Брой логове по ниво (7 дни)',
    filter_data: {
      sql: "select level, count(*) from site_logs where happened_at > now() - interval '7 days' group by level",
    },
  },
  {
    id: 2,
    name: 'Последни грешки и предупреждения',
    filter_data: {
      sql: "select * from site_logs where level in ('warn', 'error', 'critical') order by happened_at desc limit 20",
    },
  },
];

export function useLogs(filters: LogFilters) {
  return useQuery({
    queryKey: ['site_logs', filters],
    queryFn: async (): Promise<LogRow[]> => {
      if (!isConfigured || isDemoMode) {
        let list = [...MOCK_LOGS];
        if (filters.level !== '') list = list.filter((l) => l.level === filters.level);
        if (filters.source !== '') list = list.filter((l) => l.source === filters.source);
        if (filters.search !== '') {
          const s = filters.search.toLowerCase();
          list = list.filter((l) => l.message.toLowerCase().includes(s));
        }
        return list;
      }

      let query = supabase
        .from('site_logs')
        .select('id, site_id, level, category, message, context, fingerprint, source, happened_at')
        .gte('happened_at', new Date(Date.now() - filters.sinceHours * 3600_000).toISOString())
        .order('happened_at', { ascending: false })
        .limit(500);

      if (filters.level !== '') query = query.eq('level', filters.level);
      if (filters.source !== '') query = query.eq('source', filters.source);
      if (filters.category !== '') query = query.eq('category', filters.category);
      if (filters.search !== '') query = query.ilike('message', `%${filters.search}%`);
      if (filters.contextKey !== '' && filters.contextValue !== '') {
        query = query.eq(`context->>${filters.contextKey}`, filters.contextValue);
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as LogRow[];
    },
  });
}

export interface SavedQuery {
  id: number;
  name: string;
  filter_data: { sql?: string; filters?: LogFilters };
}

export function useSavedQueries() {
  return useQuery({
    queryKey: ['saved_filters', 'logs'],
    queryFn: async (): Promise<SavedQuery[]> => {
      if (!isConfigured || isDemoMode) {
        return DEMO_SAVED_QUERIES;
      }
      const { data, error } = await supabase
        .from('saved_filters')
        .select('id, name, filter_data')
        .eq('kind', 'logs')
        .order('name');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as SavedQuery[];
    },
  });
}

export function useSaveQuery() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, sql }: { name: string; sql: string }) => {
      if (!isConfigured || isDemoMode) {
        DEMO_SAVED_QUERIES.push({ id: Date.now(), name, filter_data: { sql } });
        return;
      }
      const { error } = await supabase
        .from('saved_filters')
        .upsert({ kind: 'logs', name, filter_data: { sql } }, { onConflict: 'kind,name' });
      if (error) throw new Error(error.message);
    },
    // Without this the new query did not appear in the dropdown until a remount.
    onSuccess: () => void client.invalidateQueries({ queryKey: ['saved_filters', 'logs'] }),
  });
}

export function useLogQuery() {
  return useMutation({
    mutationFn: async (sql: string): Promise<Array<Record<string, unknown>>> => {
      if (!isConfigured || isDemoMode) {
        return [
          { level: 'info', count: 48 },
          { level: 'warn', count: 6 },
          { level: 'error', count: 1 },
        ];
      }
      const { data, error } = await supabase.rpc('log_query', { p_sql: sql });
      if (error) throw new Error(error.message);
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });
}
